import { Base } from "@app/controllers/base";
import Hammer from "hammerjs";

export default class extends Base {
  static targets = ["mobileMenu", "menuButton", "hamburgerIcon", "header"]
  static values  = { galleryMode: Boolean }

  connect() {
    this._setupOverlayGestures();
    this._setupEdgeSwipe();
    this._turboHandler = () => this.close();
    document.addEventListener("turbo:before-visit", this._turboHandler);

    if (this.galleryModeValue) {
      this._initGalleryMode();
    } else {
      this._setupFooterObserver();
    }
  }

  disconnect() {
    if (this._scrollHandler) {
      window.removeEventListener("scroll", this._scrollHandler);
    }
    if (this._resetNavHandler) {
      document.removeEventListener("turbo:frame-load", this._resetNavHandler);
      document.removeEventListener("turbo:render", this._resetNavHandler);
    }
    if (this._hammerOverlay)   this._hammerOverlay.destroy();
    if (this._hammerEdge)      this._hammerEdge.destroy();
    if (this._edgeZone)        this._edgeZone.remove();
    if (this._hammerTop)       this._hammerTop.destroy();
    if (this._galleryTopZone)  this._galleryTopZone.remove();
    if (this._footerObserver)  this._footerObserver.disconnect();
    document.removeEventListener("turbo:before-visit", this._turboHandler);
    document.removeEventListener("mousemove", this._mouseHandler);
    if (this.hasHeaderTarget) {
      this.headerTarget.removeEventListener("mouseenter", this._mouseenterHandler);
      this.headerTarget.removeEventListener("mouseleave", this._mouseleaveHandler);
    }
    document.body.classList.remove("menu-open");
    clearTimeout(this._galleryTimer);
  }

  toggleMenu() {
    this.mobileMenuTarget.classList.contains("is-open") ? this.close() : this.open();
  }

  open() {
    if (!this._isMobile()) return;
    this.mobileMenuTarget.classList.add("is-open");
    this.mobileMenuTarget.setAttribute("aria-hidden", "false");
    if (this.hasMenuButtonTarget) {
      this.menuButtonTarget.setAttribute("aria-expanded", "true");
    }
    document.body.classList.add("menu-open");
    this._animateHamburger(true);
  }

  close() {
    this.mobileMenuTarget.classList.remove("is-open");
    this.mobileMenuTarget.setAttribute("aria-hidden", "true");
    if (this.hasMenuButtonTarget) {
      this.menuButtonTarget.setAttribute("aria-expanded", "false");
    }
    document.body.classList.remove("menu-open");
    this._animateHamburger(false);
  }

  // ── Footer observer ──────────────────────────────────────────────────────────

  _setupFooterObserver() {
    const footer = document.querySelector("footer");
    if (!footer) return;

    const checkVisibility = () => {
      const scrollable = document.documentElement.scrollHeight > window.innerHeight + 100;
      const scrolled = window.scrollY > 50;
      
      const rect = footer.getBoundingClientRect();
      const isIntersecting = rect.top < window.innerHeight && rect.bottom >= 0;

      this.element.classList.toggle("nav--footer-visible", scrollable && isIntersecting && scrolled);
    };

    // Run on scroll, load, and frame load
    this._scrollHandler = () => checkVisibility();
    window.addEventListener("scroll", this._scrollHandler, { passive: true });
    
    this._resetNavHandler = () => {
      this.element.classList.remove("nav--footer-visible");
      // Check again after DOM updates
      setTimeout(checkVisibility, 50);
    };
    document.addEventListener("turbo:frame-load", this._resetNavHandler);
    document.addEventListener("turbo:render", this._resetNavHandler);

    // Run initial check
    checkVisibility();
  }

  // ── Gallery mode ─────────────────────────────────────────────────────────────

  _initGalleryMode() {
    if (!this.hasHeaderTarget) return;

    // Nav renders already visible (default style, no animation on load).
    // Just start the auto-hide countdown.
    this._scheduleNavHide();

    // Desktop: mouse near top edge → reveal nav (timer only starts if nav was hidden)
    this._mouseHandler = (e) => {
      if (e.clientY < 64) {
        const wasHidden = this.headerTarget.classList.contains("nav--gallery-hidden");
        this._setNavVisible();
        if (wasHidden) this._scheduleNavHide();
      }
    };
    document.addEventListener("mousemove", this._mouseHandler);

    // While cursor is over the nav: pause the hide timer so it stays visible
    this._mouseenterHandler = () => clearTimeout(this._galleryTimer);
    this._mouseleaveHandler = () => this._scheduleNavHide();
    this.headerTarget.addEventListener("mouseenter", this._mouseenterHandler);
    this.headerTarget.addEventListener("mouseleave", this._mouseleaveHandler);

    // Mobile: swipe-down from top edge → reveal nav
    this._setupGalleryTopZone();
  }

  _setupGalleryTopZone() {
    const zone = document.createElement("div");
    Object.assign(zone.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "100%",
      height: "24px",
      zIndex: "9998",
      touchAction: "pan-x",
    });
    document.body.appendChild(zone);
    this._galleryTopZone = zone;

    this._hammerTop = new Hammer(zone, {
      recognizers: [[Hammer.Swipe, { direction: Hammer.DIRECTION_DOWN, threshold: 5, velocity: 0.2 }]],
    });
    this._hammerTop.on("swipedown", () => {
      this._setNavVisible();
      this._scheduleNavHide();
    });
  }

  _scheduleNavHide() {
    clearTimeout(this._galleryTimer);
    this._galleryTimer = setTimeout(() => this._setNavHidden(), 2500);
  }

  _setNavVisible() {
    if (!this.hasHeaderTarget) return;
    this.headerTarget.classList.remove("nav--gallery-hidden");
    this.headerTarget.classList.add("nav--gallery-visible");
    document.dispatchEvent(new CustomEvent("gallery:nav-visible"));
  }

  _setNavHidden() {
    if (!this.hasHeaderTarget) return;
    this.headerTarget.classList.remove("nav--gallery-visible");
    this.headerTarget.classList.add("nav--gallery-hidden");
    document.dispatchEvent(new CustomEvent("gallery:nav-hidden"));
  }

  // ── Private helpers ───────────────────────────────────────────────────────────

  _isMobile() {
    return window.innerWidth < 768;
  }

  _setupOverlayGestures() {
    const overlay = this.mobileMenuTarget;

    this._hammerOverlay = new Hammer.Manager(overlay, {
      recognizers: [
        [Hammer.Swipe, { direction: Hammer.DIRECTION_ALL, threshold: 10, velocity: 0.25 }],
        [Hammer.Tap],
      ],
    });

    this._hammerOverlay.on("swipe", () => this.close());

    this._hammerOverlay.on("tap", (e) => {
      if (e.target === overlay) this.close();
    });
  }

  _setupEdgeSwipe() {
    const zone = document.createElement("div");
    Object.assign(zone.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "20px",
      height: "100%",
      zIndex: "9997",
      touchAction: "pan-y",
    });
    document.body.appendChild(zone);
    this._edgeZone = zone;

    this._hammerEdge = new Hammer(zone, {
      recognizers: [[Hammer.Swipe, { direction: Hammer.DIRECTION_RIGHT }]],
    });
    this._hammerEdge.on("swiperight", () => this.open());
  }

  _animateHamburger(open) {
    if (!this.hasHamburgerIconTarget) return;
    const spans = this.hamburgerIconTarget.querySelectorAll("span");
    if (open) {
      spans[0].style.transform = "translateY(6.5px) rotate(45deg)";
      spans[1].style.opacity = "0";
      spans[2].style.transform = "translateY(-6.5px) rotate(-45deg)";
    } else {
      spans[0].style.transform = "";
      spans[1].style.opacity = "";
      spans[2].style.transform = "";
    }
  }
}
